import React, { useEffect } from 'react';
import type { WidgetProps } from '@wix/yoshi-flow-editor';
import { useSettings } from '@wix/tpa-settings/react';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import type { ControllerProps } from 'root/components/Menus/Widget/Widget';
import type { ShowcasePopulatedItem } from 'root/apiTypes';
import { dataHooks } from 'root/dataHooks';
import { settingsParams } from 'root/components/MenuAddOn/settingsParams';
import type { ItemsToShow } from 'root/components/MenuAddOn/Settings/Tabs/MenusTab/consts';
import { WidgetType } from 'root/components/MenuAddOn/Settings/Tabs/MenusTab/consts';
import { SLIDER_SCROLL_ANIMATION_DURATION_MS } from 'root/components/MenuAddOn/Settings/Tabs/LayoutTab/consts';
import { Items } from 'root/components/Menus/Items/Items';
import { MenuState } from 'root/components/Menus/MenuState/MenuState';
import { Text, TextTypography } from 'wix-ui-tpa/cssVars';
import { ChevronLeft, ChevronRight } from '@wix/wix-ui-icons-common';
import { classes, st } from './Widget.st.css';
import { getMenuAddOnDefaultText } from 'root/components/MenuAddOn/defaultText';
import type { ItemsBehavior } from 'root/contexts/ItemsContext';
import type {
  MenuAddOnAddToCartData,
  MenuAddOnOrderingBanner,
  MenuAddOnOrderingPageConflict,
} from 'root/components/MenuAddOn/types';
import { getOrderingBannerToDisplay, OrderingBanner, shouldDisplayOrderingBanner } from './OrderingBanner';
import { OrderingPageConflictModal } from './OrderingPageConflictModal';
import { useGetHtmlTag } from 'root/hooks/useGetHtmlTag';
import { useMenuAddOnWidgetStyles } from './useMenuAddOnWidgetStyles';
import {
  getMenuAddOnEffectiveSelectedMenuId,
  getMenuAddOnSelectedMenus,
  getMenuAddOnVisibleMenus,
  isMenuAddOnStoredSelectionValid,
} from 'root/components/MenuAddOn/visibleMenus';

export type MenuAddOnControllerProps = ControllerProps & {
  addItemToCart?: (data: MenuAddOnAddToCartData) => Promise<void>;
  navigateToMenusPage?: () => void;
  navigateToOrderingPage?: () => void;
  onChangeOrderingDispatch?: () => Promise<void>;
  onClearOrderingPageConflictCart?: () => Promise<void>;
  onCloseOrderingPageConflict?: () => void;
  onLoadMoreClick?: (data: { itemsShownCount: number; totalItemsCount: number }) => void;
  onSliderArrowClick?: (direction: 'previous' | 'next') => void;
  onViewCart?: () => Promise<void>;
  cartQuantities?: Record<string, number>;
  orderingBanner?: MenuAddOnOrderingBanner;
  orderingPageConflict?: MenuAddOnOrderingPageConflict | null;
  isOrderingAppInstalled?: boolean;
  isLoading?: boolean;
};

export type { ControllerProps };

export const Widget = (props: WidgetProps<MenuAddOnControllerProps>) => {
  const { t } = useTranslation();
  const { isEditor } = useEnvironment();
  // State (not a ref) so the slider effect re-runs when the items layout mounts after the loading/placeholder render.
  const [sliderRoot, setSliderRoot] = React.useState<HTMLDivElement | null>(null);
  const sliderScrollTimeoutRef = React.useRef<number | undefined>(undefined);
  const [canScrollPrevious, setCanScrollPrevious] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  const settings = useSettings();
  const addItemToCart = props.addItemToCart;
  const menus = props.menus ?? [];
  const isLoading = props.isLoading ?? !props.menus;

  const widgetType = settings.get(settingsParams.widgetType) as WidgetType;
  const storedSelectedMenuId = settings.get(settingsParams.selectedMenuId) as string;
  const itemsToShow = settings.get(settingsParams.itemsToShow) as ItemsToShow;
  const selectedSectionId = settings.get(settingsParams.selectedSectionId) as string;
  const selectedRibbon = settings.get(settingsParams.selectedRibbon) as string;
  const selectedItemIdsRaw = settings.get(settingsParams.selectedItemIds) as string;
  const selectedItemIds = selectedItemIdsRaw ? selectedItemIdsRaw.split(',').filter(Boolean) : [];
  const showViewFullMenuButton = settings.get(settingsParams.showViewFullMenuButton) as boolean;
  const showAddToCartButton = settings.get(settingsParams.showAddToCartButton) as boolean;
  const menuTitleText = settings.get(settingsParams.menuTitleText) as string;
  const menuSubtitleText = settings.get(settingsParams.menuSubtitleText) as string;
  const menuTitleHtmlTag = useGetHtmlTag('menuTitleTheme');
  const menuDescriptionHtmlTag = useGetHtmlTag('menuDescriptionTheme');
  const viewFullMenuButtonText = settings.get(settingsParams.viewFullMenuButtonText) as string;
  const addToCartButtonText = settings.get(settingsParams.addToCartButtonText) as string;
  const showImagePlaceholder = settings.get(settingsParams.showImagePlaceholder) as boolean;

  const {
    AddToCartIcon,
    addToCartButtonFullWidth,
    featuredBadgePlacement,
    isSliderItemsLayout,
    itemImageResize,
    itemsColumnGap,
    itemsPerLoad,
    maxItemsPerRow,
    shouldShowAddToCartIcon,
    shouldShowAddToCartLabel,
    showItemImage,
    showSubtitle,
    showTitle,
  } = useMenuAddOnWidgetStyles();

  const getSliderTrack = React.useCallback(
    () => sliderRoot?.querySelector<HTMLElement>(`[data-hook="${dataHooks.items.container}"]`) ?? null,
    [sliderRoot]
  );

  const updateSliderControls = React.useCallback(() => {
    const sliderTrack = getSliderTrack();

    if (!sliderTrack) {
      setCanScrollPrevious(false);
      setCanScrollNext(false);
      return;
    }

    const maxScrollLeft = sliderTrack.scrollWidth - sliderTrack.clientWidth;
    setCanScrollPrevious(sliderTrack.scrollLeft > 1);
    setCanScrollNext(sliderTrack.scrollLeft < maxScrollLeft - 1);
  }, [getSliderTrack]);

  const scrollSlider = (direction: -1 | 1) => {
    const sliderTrack = getSliderTrack();

    if (!sliderTrack) {
      return;
    }

    props.onSliderArrowClick?.(direction === -1 ? 'previous' : 'next');

    sliderTrack.scrollBy({
      behavior: 'smooth',
      left: direction * (sliderTrack.clientWidth + itemsColumnGap),
    });

    if (sliderScrollTimeoutRef.current) {
      window.clearTimeout(sliderScrollTimeoutRef.current);
    }

    sliderScrollTimeoutRef.current = window.setTimeout(() => {
      updateSliderControls();
      sliderScrollTimeoutRef.current = undefined;
    }, SLIDER_SCROLL_ANIMATION_DURATION_MS);
  };

  const handleViewFullMenuClick = () => {
    if (widgetType === WidgetType.Ordering) {
      props.navigateToOrderingPage?.();
      return;
    }

    props.navigateToMenusPage?.();
  };

  const isStoredSelectionValid = isMenuAddOnStoredSelectionValid(menus, storedSelectedMenuId);
  const effectiveSelectedMenuId = getMenuAddOnEffectiveSelectedMenuId(menus, storedSelectedMenuId);
  const selectedMenus = getMenuAddOnSelectedMenus(menus, storedSelectedMenuId);
  const filteredMenus = getMenuAddOnVisibleMenus(menus, {
    discountInfoByItemId: props.discountInfoByItemId,
    isOrderingAppInstalled: props.isOrderingAppInstalled,
    itemsToShow,
    selectedItemIds,
    selectedMenuId: storedSelectedMenuId,
    selectedRibbon,
    selectedSectionId,
    widgetType,
  });

  const allItems: ShowcasePopulatedItem[] = filteredMenus.flatMap((menu) =>
    menu.sections.flatMap((section) => section.items ?? [])
  );
  const addToCartContextByItemId = selectedMenus.reduce((contextByItemId, menu) => {
    menu.sections.forEach((section) => {
      (section.items ?? []).forEach((item) => {
        if (!item.id) {
          return;
        }

        contextByItemId.set(item.id, {
          businessLocationId: menu.businessLocationId,
          menuId: menu.id,
          sectionId: section.id,
        });
      });
    });

    return contextByItemId;
  }, new Map<string, Omit<MenuAddOnAddToCartData, 'item'>>());
  const handleAddToCart = React.useCallback(
    async (item: ShowcasePopulatedItem) => {
      if (item.orderSettings?.inStock === false) {
        return;
      }

      const itemContext = item.id ? addToCartContextByItemId.get(item.id) : undefined;

      await addItemToCart?.({
        ...itemContext,
        item,
      });
    },
    [addItemToCart, addToCartContextByItemId]
  );
  const itemBehavior = React.useMemo<ItemsBehavior>(
    () => ({
      canPlaceFeaturedBadgeOnImage: showItemImage,
      disableImageExpand: widgetType === WidgetType.Ordering,
      featuredBadgePlacement,
      hideModifierGroups: widgetType === WidgetType.Ordering,
      hidePriceVariants: widgetType === WidgetType.Ordering,
      imageResize: itemImageResize,
      showImagePlaceholder,
      cartQuantityByItemId: props.cartQuantities,
      action:
        widgetType === WidgetType.Ordering && showAddToCartButton
          ? {
              ariaLabel: addToCartButtonText || getMenuAddOnDefaultText('addToCartButton', t),
              fullWidth: addToCartButtonFullWidth,
              label: addToCartButtonText || getMenuAddOnDefaultText('addToCartButton', t),
              prefixIcon: shouldShowAddToCartIcon ? <AddToCartIcon /> : undefined,
              showLabel: shouldShowAddToCartLabel,
              onClick: handleAddToCart,
            }
          : undefined,
    }),
    [
      AddToCartIcon,
      addToCartButtonFullWidth,
      addToCartButtonText,
      featuredBadgePlacement,
      handleAddToCart,
      itemImageResize,
      showAddToCartButton,
      showImagePlaceholder,
      showItemImage,
      props.cartQuantities,
      shouldShowAddToCartIcon,
      shouldShowAddToCartLabel,
      t,
      widgetType,
    ]
  );

  const shouldLoadItemsInPortions = !isSliderItemsLayout;
  const [visibleItemsCount, setVisibleItemsCount] = React.useState(itemsPerLoad);

  // Runs in both display modes: Grid vs Slider is switched by CSS vars (see `layoutCssVars.ts`), which Classic Editor
  // applies without re-rendering. The ResizeObserver catches that switch (the track's height changes) so the arrows
  // and their position follow; in Grid the track never overflows, so both arrows stay off.
  useEffect(() => {
    const sliderTrack = getSliderTrack();

    const updateSliderButtonTop = () => {
      const sliderImage = sliderRoot?.querySelector<HTMLElement>(
        [
          `[data-hook="${dataHooks.image.clickableImage(dataHooks.item.image)}"]`,
          `[data-hook="${dataHooks.item.reservedImageContainer}"]`,
        ].join(', ')
      );

      if (!sliderRoot || !sliderImage) {
        return;
      }

      const sliderRect = sliderRoot.getBoundingClientRect();
      const imageRect = sliderImage.getBoundingClientRect();
      sliderRoot.style.setProperty(
        '--menuAddOnSliderButtonTop',
        `${imageRect.top - sliderRect.top + imageRect.height / 2}px`
      );
    };

    const sliderButtonTopAnimationFrame = window.requestAnimationFrame(updateSliderButtonTop);

    updateSliderControls();
    updateSliderButtonTop();

    const handleTrackResize = () => {
      updateSliderControls();
      updateSliderButtonTop();
    };
    const trackResizeObserver =
      sliderTrack && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(handleTrackResize) : undefined;

    if (sliderTrack) {
      trackResizeObserver?.observe(sliderTrack);
    }
    sliderTrack?.addEventListener('scroll', updateSliderControls);
    window.addEventListener('resize', updateSliderControls);
    window.addEventListener('resize', updateSliderButtonTop);

    return () => {
      window.cancelAnimationFrame(sliderButtonTopAnimationFrame);

      if (sliderScrollTimeoutRef.current) {
        window.clearTimeout(sliderScrollTimeoutRef.current);
        sliderScrollTimeoutRef.current = undefined;
      }

      trackResizeObserver?.disconnect();
      sliderTrack?.removeEventListener('scroll', updateSliderControls);
      window.removeEventListener('resize', updateSliderControls);
      window.removeEventListener('resize', updateSliderButtonTop);
    };
  }, [getSliderTrack, isSliderItemsLayout, maxItemsPerRow, sliderRoot, updateSliderControls, visibleItemsCount]);

  useEffect(() => {
    setVisibleItemsCount(itemsPerLoad);
  }, [
    allItems.length,
    effectiveSelectedMenuId,
    itemsPerLoad,
    itemsToShow,
    selectedItemIdsRaw,
    selectedRibbon,
    selectedSectionId,
    widgetType,
  ]);

  const visibleItems = shouldLoadItemsInPortions ? allItems.slice(0, visibleItemsCount) : allItems;
  const showLoadMoreButton = shouldLoadItemsInPortions && visibleItemsCount < allItems.length;
  const hasItems = allItems.length > 0;
  const isOrderingAppUnavailable = widgetType === WidgetType.Ordering && props.isOrderingAppInstalled === false;
  const hasLoadedMenusWithoutValidSelection = !isEditor && menus.length > 0 && !isStoredSelectionValid;
  const shouldShowPlaceholder =
    Boolean(props.showPaywallPlaceholder) ||
    Boolean(props.messageError) ||
    isOrderingAppUnavailable ||
    !hasItems ||
    hasLoadedMenusWithoutValidSelection;
  const shouldShowItems = !shouldShowPlaceholder;
  const shouldShowFooterControls = shouldShowItems && (showLoadMoreButton || showViewFullMenuButton);
  const orderingBanner = getOrderingBannerToDisplay({
    banner: props.orderingBanner,
    isEditor,
    menus,
    t,
    widgetType,
  });
  const shouldShowOrderingBanner = shouldDisplayOrderingBanner(widgetType, orderingBanner);

  if (isLoading) {
    return null;
  }

  const isEmptyItemsState = !props.showPaywallPlaceholder && !props.messageError && !isOrderingAppUnavailable;

  const menuStateContent =
    (props.showPaywallPlaceholder
      ? t('component.menu.paywall-placeholder')
      : props.messageError ||
        (isOrderingAppUnavailable
          ? t('app.menu-add-on.widget.ordering-unavailable')
          : t('app.menu-add-on.widget.empty-state'))) || '';

  return (
    <div className={st(classes.root, shouldShowOrderingBanner ? classes.rootWithOrderingBanner : '')}>
      {shouldShowItems && (
        <>
          {(menuTitleText || menuSubtitleText) && (
            // Visibility of each line is a CSS display toggle (`--menuAddOnDesignTitleDisplay` /
            // `--menuAddOnDesignSubtitleDisplay`, see `designCssVars.ts`), not a JS condition: Classic Editor
            // applies style-only changes as CSS vars without re-rendering, so gating here on `showTitle`/
            // `showSubtitle` would leave the toggle stale until a reload.
            <div className={classes.header}>
              {menuTitleText && (
                <Text
                  className={classes.title}
                  id="menu-add-on-title"
                  tagName={menuTitleHtmlTag}
                  typography={TextTypography.largeTitle}
                >
                  {menuTitleText}
                </Text>
              )}
              {menuSubtitleText && (
                <Text
                  className={classes.subtitle}
                  tagName={menuDescriptionHtmlTag}
                  typography={TextTypography.runningText}
                >
                  {menuSubtitleText}
                </Text>
              )}
            </div>
          )}
          <div ref={setSliderRoot} className={classes.itemsLayout}>
            <div className={classes.itemsSliderViewport}>
              <Items
                discountInfoByItemId={props.discountInfoByItemId}
                itemBehavior={itemBehavior}
                items={visibleItems}
                sectionId="menu-add-on"
                sectionTitleId="menu-add-on-title"
              />
            </div>
            {canScrollPrevious && (
              <button
                aria-label={t('app.menu-add-on.widget.slider.previous')}
                className={st(classes.sliderButton, classes.sliderButtonPrevious)}
                data-hook={dataHooks.menuAddOnWidget.sliderPreviousButton}
                type="button"
                onClick={() => scrollSlider(-1)}
              >
                <ChevronLeft />
              </button>
            )}
            {canScrollNext && (
              <button
                aria-label={t('app.menu-add-on.widget.slider.next')}
                className={st(classes.sliderButton, classes.sliderButtonNext)}
                data-hook={dataHooks.menuAddOnWidget.sliderNextButton}
                type="button"
                onClick={() => scrollSlider(1)}
              >
                <ChevronRight />
              </button>
            )}
          </div>
        </>
      )}
      {shouldShowPlaceholder && (
        <MenuState
          content={menuStateContent}
          subtitle={isEmptyItemsState && showSubtitle ? menuSubtitleText : undefined}
          title={isEmptyItemsState && showTitle ? menuTitleText : undefined}
        />
      )}
      {shouldShowFooterControls && (
        <div className={classes.footerControls}>
          {showLoadMoreButton && (
            <div className={classes.loadMoreButtonRow}>
              <button
                className={classes.loadMoreButton}
                type="button"
                onClick={() =>
                  setVisibleItemsCount((count) => {
                    const nextCount = Math.min(count + itemsPerLoad, allItems.length);
                    props.onLoadMoreClick?.({ itemsShownCount: nextCount, totalItemsCount: allItems.length });

                    return nextCount;
                  })
                }
              >
                {t('app.menu-add-on.widget.load-more')}
              </button>
            </div>
          )}
          {showViewFullMenuButton && (
            <div className={classes.viewFullMenuButtonRow}>
              <button className={classes.viewFullMenuButton} type="button" onClick={handleViewFullMenuClick}>
                {viewFullMenuButtonText || getMenuAddOnDefaultText('viewFullMenuButton', t)}
              </button>
            </div>
          )}
        </div>
      )}
      {shouldShowOrderingBanner && (
        <OrderingBanner
          banner={props.orderingBanner}
          isEditor={isEditor}
          menus={menus}
          widgetType={widgetType}
          onChangeOrderingDispatch={props.onChangeOrderingDispatch}
          onViewCart={props.onViewCart}
        />
      )}
      <OrderingPageConflictModal
        conflict={props.orderingPageConflict}
        onClearCart={async () => {
          // props is a live proxy; reading at click time picks up the latest setProps callback.
          await props?.onClearOrderingPageConflictCart?.();
        }}
        onClose={() => {
          // Passing props.onCloseOrderingPageConflict directly would capture a stale reference.
          props?.onCloseOrderingPageConflict?.();
        }}
      />
    </div>
  );
};

export default Widget;
